import { flatMap, map } from "lodash";
import { useActivities } from "../../api/activitystreams";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import Activity from "./Activity";
import GenericErrorBoundary from "../shared/GenericErrorBoundary.tsx";
import PageLoading from "../shared/PageLoading";

type Props = {
  streamUrl: string;
};

// Read-only render of an externally-visible activitystream, used for both the
// browser and the mobile app. Reuses the standard Activity components (via
// readOnly, which hides composer/interactions/replies) and the shared
// useActivities hook. The backend serializes external streams without a member,
// so permissions come back all-false. The borderless app look follows for free
// from `.tixxt--in-app .activity` (mobile_app.css), exactly like normal streams.
export default function ExternalActivitystream({ streamUrl }: Props) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useActivities(streamUrl);

  const activities = flatMap(data?.pages, (page) => page.data) ?? [];

  const loadMoreRef = useInfiniteScroll(
    () => {
      if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    },
    {
      disabled: !hasNextPage || isFetchingNextPage,
      rootMargin: "400px",
    },
  );

  if (isLoading) return <PageLoading />;

  return (
    <div className="external-activitystream flex flex-col gap-4">
      {map(activities, (activity, index) => (
        <GenericErrorBoundary key={activity?.id ?? index}>
          <Activity
            activity={activity}
            streamUrl={streamUrl}
            readOnly
            external
          />
        </GenericErrorBoundary>
      ))}
      <div ref={loadMoreRef} style={{ height: "1px", visibility: "hidden" }} />
      {isFetchingNextPage && <PageLoading />}
    </div>
  );
}
